import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserIcon } from '@heroicons/react/24/outline';
import Button from '../../components/ui/Button';
import FormInput from '../../components/ui/FormInput';
import { useCustomRequestPasswordResetMutation } from '../../redux/apis/endpoints/authApi';

const ForgotPassword = () => {
  const location = useLocation();
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // RTK Query hook for password reset request - using custom endpoint
  const [requestPasswordReset, { isLoading, error: resetError }] = useCustomRequestPasswordResetMutation();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update error state when RTK Query error changes
  useEffect(() => {
    if (resetError) {
      setError(
        resetError.data?.error?.message || 
        resetError.data?.message || 
        'Failed to send reset email. Please try again later.'
      );
    }
  }, [resetError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      // Using our custom endpoint now
      await requestPasswordReset(identifier).unwrap();
      setSuccessMessage('Reset password instructions have been sent to your email.');
      setIdentifier('');
    } catch (err) {
      // Error is handled by the useEffect above
      console.error('Forgot password error:', err);
    }
  };

  // Check if this is a closet user from URL params
  const urlParams = new URLSearchParams(location.search);
  const isClosetUser = urlParams.get('isClosetUser');

  // Determine background style
  const backgroundStyle = isClosetUser
    ? {
        minHeight: '100vh',
        backgroundImage:
          "url('https://res.cloudinary.com/sheyou/image/upload/v1727875894/virtual_closet_5d66ccc54e.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : {
        minHeight: '100vh'
      };

  // Common input style for consistency
  const inputStyle = "text-gray-900 placeholder:text-gray-400";

  return (
    <div className="gradient_limeade flex flex-col" style={backgroundStyle}>
      <div className="flex flex-grow items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow-md">
          <div>
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              Reset your password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your username or email address and we'll send you a link to reset your password.
            </p>
          </div>
          
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {successMessage && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Success</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>{successMessage}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <FormInput
                label="Username or Email"
                id="identifier"
                name="identifier"
                type="text"
                autoComplete="username email"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter your username or email"
                leftIcon={<UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                className={inputStyle}
                fluid={true}
              />
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <Button
                type="submit"
                fullWidth
                isLoading={isLoading}
                variant="teal"
                size="lg"
              >
                Send reset instructions
              </Button>
              
              <div className="text-center mt-2">
                <Link 
                  to={`/login${isClosetUser ? `?isClosetUser=${isClosetUser}` : ''}`} 
                  className="font-medium text-brand-teal hover:text-brand-teal-light"
                >
                  Back to login
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 